import { Injectable } from '@angular/core';
import { AuthService } from 'app/core/auth.service';
import { DAO } from 'app/shared-services/db-access/dao';
import { DataConstants } from 'app/shared/consts/dataConstants';
import { GroupType } from 'app/groups/models/group-type';
import { Observable, combineLatest, of } from 'rxjs';
import { map, mergeMap, take } from 'rxjs/operators';
import { ForumPost, IForumPost } from '../forum/models/forum-post';
import { ForumTopic } from '../forum/models/forum-topic';
import { Upload } from 'app/shared/models';
import { StorageService } from 'app/core/storage.service';
import { isNil } from 'app/shared/common/acaLodash';
import { Group } from 'app/groups/models/group';
import { PostRelation } from 'app/forum/models/post-relation';
@Injectable({
  providedIn: 'root',
})
export class PostService {
  private userId: string;
  uploadsMarkedForDeletion: Upload[] = [];
  constructor(
    private dao: DAO,
    private authService: AuthService,
    private storageService: StorageService
  ) {
    this.authService.getCurrentUser$().subscribe((user) => {
      if (user) {
        this.userId = user.uid;
      } else {
        this.userId = undefined;
      }
    });
  }

  public setNewPostProperties(post: ForumPost, group: Group): ForumPost {
    post.key = this.dao.createPushId();
    post.ownerId = this.userId;
    post.forumId = group.forumId;
    post.created = new Date();
    post.groupId = group.key;
    post.groupType = group.groupType;
    post.lastUpdate = new Date();
    post.latestComment = new Date();
    post.topic = new ForumTopic();
    post.taggedUserIds = [];
    post.teaser = group.permissions.premium;
    post.plannedAt = null;
    return post;
  }

  private async removeFileFromStorage(index, arr: Upload[]) {
    await this.storageService
      .deleteFile(arr[index].path, true)
      .toPromise()
      .then(() => {
        arr.splice(index, 1);
      })
      .catch((e) => {
        if (this.uploadsMarkedForDeletion.length !== 0) {
          arr.splice(index, 1);
        }
      });
  }

  cleanDeletedAttachments() {
    this.uploadsMarkedForDeletion.forEach(async (val, index, arr) => {
      await this.removeFileFromStorage(index, arr);
    });

    this.uploadsMarkedForDeletion = [];
  }

  public async createPost(post: ForumPost, group?: Group): Promise<ForumPost> {
    if (post && post.forumId) {
      if (isNil(post.key)) {
        console.error('post.key is undefined');
        return;
      }

      await this.cleanDeletedAttachments();

      await this.dao
        .ref(`${DataConstants.FORUM_POSTS}${post.forumId}/${post.key}`)
        .set({ releation: 'owned' });
      await this.dao
        .ref(`${DataConstants.POSTS}${post.key}`)
        .set(ForumPost.toJson(post));
      return post;
    }
  }

  public async removePlannedPost(
    post: ForumPost,
    group?: Group
  ): Promise<void> {
    if (!post || !post.key || !post.forumId || !post.ownerId) {
      return;
    }

    try {
      await this.dao
        .ref(`${DataConstants.PLANNED_FORUM_POSTS}${post.forumId}/${post.key}`)
        .remove();

      await this.dao
        .ref(`${DataConstants.PLANNED_USER_POSTS}${post.ownerId}/${post.key}`)
        .remove();

      await this.dao.ref(`${DataConstants.PLANNED_POSTS}${post.key}`).remove();
    } catch (error) {
      console.error('Error deleting planned post:', error);
    }
  }

  public async createPlannedPost(
    post: ForumPost,
    group?: Group
  ): Promise<ForumPost> {
    if (post && post.forumId && post.plannedAt) {
      if (isNil(post.key)) {
        console.error('post.key is undefined');
        return;
      }

      await this.cleanDeletedAttachments();

      await this.dao
        .ref(`${DataConstants.PLANNED_FORUM_POSTS}${post.forumId}/${post.key}`)
        .set({ releation: 'owned' });
      await this.dao
        .ref(`${DataConstants.PLANNED_USER_POSTS}${post.ownerId}/${post.key}`)
        .set(PostRelation.Owner);
      await this.dao
        .ref(`${DataConstants.PLANNED_POSTS}${post.key}`)
        .set(ForumPost.toJson(post));
      return post;
    }
  }

  public async updatePost(post: ForumPost): Promise<ForumPost> {
    if (post && post.key) {
      post.lastUpdate = new Date();
      await this.cleanDeletedAttachments();

      await this.dao
        .ref(`${DataConstants.POSTS}${post.key}`)
        .set(ForumPost.toJson(post));
      return post;
    }
  }

  public getPost$(postId: string): Observable<ForumPost> {
    return this.dao
      .object$<IForumPost<number>>(`${DataConstants.POSTS}${postId}`)
      .pipe(map((post) => this.getPostOrNull(post, postId)));
  }

  private getPostOrNull(post, postId: string): ForumPost {
    if (post) {
      this.incrementViewCount(postId);
      return ForumPost.fromJson(post);
    }
  }

  private incrementViewCount(postId: string) {
    const viewCountRef = this.dao.object<number>(
      `${DataConstants.POST_VIEWS}/${postId}`
    );
    viewCountRef
      .valueChanges()
      .pipe(take(1))
      .subscribe((currentViewCount) => {
        const updatedViewCount = (currentViewCount || 0) + 1;
        viewCountRef.set(updatedViewCount).catch((error) => {
          console.error('Error updating view count: ', error);
        });
      });
  }

  public getPostViewCount(postId: string): Observable<number> {
    return this.dao
      .object<number>(`${DataConstants.POST_VIEWS}/${postId}`)
      .valueChanges();
  }

  public getPosts$(
    forumId: string,
    batch: number,
    lastKey?: string
  ): Observable<Array<ForumPost>> {
    return this.getPostIds$(forumId, batch, lastKey).pipe(
      mergeMap((postIds) =>
        combineLatest(postIds.reverse().map((postId) => this.getPost$(postId)))
      )
    );
  }

  private getPostIds$(
    forumId: string,
    batch: number,
    lastKey?: string
  ): Observable<Array<string>> {
    return this.dao
      .listData$(`${DataConstants.FORUM_POSTS}${forumId}`, (ref) => {
        if (lastKey) {
          return ref.orderByKey().limitToLast(batch).endAt(lastKey);
        } else {
          return ref.orderByKey().limitToLast(batch);
        }
      })
      .pipe(map((posts) => posts.map((post) => post.key)));
  }

  public getAllUserPlannedPosts(): Observable<ForumPost[]> {
    if (!this.userId) return of([]);

    return this.dao
      .listData$(`${DataConstants.PLANNED_USER_POSTS}${this.userId}`)
      .pipe(
        map((posts) => posts.map((p) => p.key)),
        mergeMap((postIds) =>
          postIds.length
            ? combineLatest(postIds.map((id) => this.getPlannedPost$(id)))
            : of([])
        ),
        map((posts) => posts.filter((p) => !!p))
      );
  }

  public getPlannedPosts$(
    forumId: string,
    batch: number,
    lastKey?: string
  ): Observable<Array<ForumPost>> {
    return this.getPlannedPostIds$(forumId, batch, lastKey).pipe(
      mergeMap((postIds) =>
        combineLatest(
          postIds.reverse().map((postId) => this.getPlannedPost$(postId))
        )
      ),
      map((posts) => posts.filter((p) => !!p))
    );
  }

  public getPlannedPost$(postId: string): Observable<ForumPost> {
    return this.dao
      .object$<IForumPost<number>>(`${DataConstants.PLANNED_POSTS}${postId}`)
      .pipe(
        map((post) => {
          if (post) {
            return ForumPost.fromJson(post);
          } else {
            return null;
          }
        })
      );
  }

  private getPlannedPostIds$(
    forumId: string,
    batch: number,
    lastKey?: string
  ): Observable<Array<string>> {
    return this.dao
      .listData$(`${DataConstants.PLANNED_FORUM_POSTS}${forumId}`, (ref) => {
        if (lastKey) {
          return ref.orderByKey().limitToLast(batch).endAt(lastKey);
        } else {
          return ref.orderByKey().limitToLast(batch);
        }
      })
      .pipe(map((posts) => posts.map((post) => post.key)));
  }

  public getPublicPosts$(
    groupType: GroupType,
    batch: number,
    lastKey?: string
  ): Observable<Array<ForumPost>> {
    return this.dao
      .listData$(`${DataConstants.GROUP_PUBLIC_POSTS}${groupType}`, (ref) => {
        if (lastKey) {
          return ref.orderByKey().limitToLast(batch).endAt(lastKey);
        } else {
          return ref.orderByKey().limitToLast(batch);
        }
      })
      .pipe(
        map((posts) =>
          posts
            .reverse()
            .map((post) =>
              this.getPostOrNull(post.data as IForumPost<number>, post.key)
            )
        )
      );
  }

  public getUserRelatedPosts$(
    batch: number,
    lastKey?: string
  ): Observable<ForumPost[]> {
    return this.dao
      .listData$(`${DataConstants.USER_POSTS}${this.userId}`, (ref) => {
        if (lastKey) {
          return ref.orderByKey().limitToLast(batch).endAt(lastKey);
        } else {
          return ref.orderByKey().limitToLast(batch);
        }
      })
      .pipe(
        mergeMap((postIds) =>
          combineLatest(
            postIds.reverse().map((postId) => this.getPost$(postId.key))
          )
        )
      );
  }
}
